<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import {
        CreateOrUpdateOrDeleteReactionMutationDocument,
        type ParticipantInput,
        type ReactionFragment,
        type ReactionTargetInput,
        type CreateOrUpdateOrDeleteReactionMutationMutation,
    } from '$gql/graphql';
    import { cx } from '$lib/utils/cx.js';
    import { hover, isTouchDevice, longtouch } from '$lib/utils/interractions.js';
    import ReactionSelector from './ReactionSelector.svelte';
    import { getReactionsData } from './Utils';
    import { fade, fly } from 'svelte/transition';
    import * as t from '$trans';

    const { apollo, datalayer, lang } = getContainer();

    export let reaction: ReactionFragment | null | undefined;
    export let target: ReactionTargetInput;
    export let author: ParticipantInput;
    export let hideButtonLabel: boolean = false;
    export let className: string = '';
    export let onReactionsCounters:
        | ((
              counters: CreateOrUpdateOrDeleteReactionMutationMutation['reaction']['reaction']['reactionsCounters'],
          ) => void)
        | null = null;

    let reactionType: string | null = reaction?.type ?? null;

    let showReactionBar = false;
    //let loading = false;
    let loadingType: string | null = null;

    const save = async (type: string) => {
        loadingType = type;

        const _result = await apollo().mutate({
            mutation: CreateOrUpdateOrDeleteReactionMutationDocument,
            variables: {
                type,
                target,
                author,
            },
            refetchQueries: [],
        });

        loadingType = null;

        let reaction = _result.data?.reaction.reaction.reaction;
        let reactionsCounters = _result.data?.reaction.reaction.reactionsCounters;

        onReactionsCounters?.(reactionsCounters ?? []);

        reactionType = reaction?.type ?? null;

        if (reaction != null) {
            datalayer()?.push({
                event: 'reaction',
                reaction: {
                    type: reaction.type,
                    target,
                },
            });
        }
    };

    function onHover(e: CustomEvent<boolean>) {
        if (window.innerWidth >= 1024 || !isTouchDevice())
            // ls breakpoint
            showReactionBar = e.detail;
    }

    $: reactionsData = getReactionsData($lang);
    $: _loadingType = loadingType as keyof typeof reactionsData.colors;
    $: _type = reactionType as keyof typeof reactionsData.colors;
</script>

<div use:hover={{ delayIn: 250, delayOut: 250 }} on:hover={onHover}>
    {#if showReactionBar}
        <div in:fly={{ y: 10, duration: 300 }} out:fade class={'relative'}>
            <div class={'absolute bottom-4'}>
                <ReactionSelector
                    on:select={(event) => {
                        showReactionBar = false;
                        save(event.detail);
                    }}
                    on:close={() => (showReactionBar = false)}
                />
            </div>
            <div class={'w-[300%] absolute bg-transparent -bottom-2 h-8'}></div>
        </div>
    {/if}
    <button
        disabled={loadingType != null}
        use:longtouch={300}
        on:longtouch={() => {
            showReactionBar = true;
        }}
        on:click={() => {
            showReactionBar = false;
            save(reactionType ? reactionType : 'like');
        }}
        type={'button'}
        class={cx(
            'bg-transparent hover:bg-gray-200 cursor-pointer py-3 px-5 text-black rounded-full focus:outline-none focus:ring-2 inline-flex items-center',
            'border-none',
            'h-full',
            className,
        )}
    >
        {#if loadingType != null}
            <span
                class={'inline-flex items-center space-x-2'}
                style={`color: ${reactionsData.colors[_loadingType]};`}
            >
                {#if loadingType === 'like'}
                    <i class={'fa-lg fa fa-thumbs-up'} />
                {:else}
                    <img
                        src={reactionsData.images[_loadingType]}
                        alt={reactionsData.labels[_loadingType]}
                        class={'h-8 w-8'}
                    />
                {/if}

                {#if !hideButtonLabel}
                    <span class={'font-semibold'}>
                        {reactionsData.labels[_loadingType]}
                    </span>
                {/if}
            </span>
        {:else if reactionType}
            <span
                class={'inline-flex items-center space-x-2'}
                style={`color: ${reactionsData.colors[_type]};`}
            >
                {#if reactionType === 'like'}
                    <i class={'fa-lg fa fa-thumbs-up'} />
                {:else}
                    <img
                        src={reactionsData.images[_type]}
                        alt={reactionsData.labels[_type]}
                        class={'h-8 w-8'}
                    />
                {/if}

                {#if !hideButtonLabel}
                    <span class={'font-semibold'}>
                        {reactionsData.labels[_type]}
                    </span>
                {/if}
            </span>
        {:else}
            <span class={'inline-flex items-center space-x-2'}>
                <i class={'fa-lg fa fa-thumbs-up'} />{' '}
                {#if !hideButtonLabel}
                    <span>
                        {t.feed_post.button_to_like({}, $lang)}
                    </span>
                {/if}
            </span>
        {/if}
    </button>
</div>
